import React, { useEffect, useImperativeHandle, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell, TableSkeleton, Amount, RowActions, RowAction, ListEmptyRow,
  StatCard, ConfirmModal, Input, LoadingButton, DialogShell, DialogHeader, DialogFooter, dialogButtonClass, FormFieldWithError,
} from '@redpanda/ui';
import { formatTableDate, useNumberInput } from '@redpanda/utils';
import { useExpensesCache } from '../stores/timeEntryStore';
import { useProfitability } from './ProjectTimePanel';
import { deleteExpense, setCostBudget } from '../services/api';
import { notifyDataChanged, useDataChanged } from '../utils/dataEvents';
import { PMExpense, PMPackage } from '../types/pm';
import ExpenseDialog from './ExpenseDialog';

export interface ProjectExpensesPanelRef {
  openCreateDialog: () => void;
}

interface Props {
  projectId: string;
  packages: Array<Pick<PMPackage, 'id' | 'name'>>;
}

/** 成本預算用量的顏色：80% 以上警示、100% 以上超支 */
const usageTone = (usage: number) =>
  usage >= 100 ? 'text-red-600 dark:text-red-400' : usage >= 80 ? 'text-amber-700 dark:text-amber-400' : '';
const barTone = (usage: number) => (usage >= 100 ? 'bg-red-500' : usage >= 80 ? 'bg-amber-500' : 'bg-accent-fill');

/** 設定成本預算（空白＝不設） */
const CostBudgetDialog: React.FC<{ projectId: string; current: number | null; onClose: () => void }> = ({ projectId, current, onClose }) => {
  const { t } = useTranslation(['translation', 'pages']);
  const { register, handleSubmit, formState: { errors } } = useForm<{ costBudget: number | '' }>({ defaultValues: { costBudget: current ?? '' } });
  const budgetInput = useNumberInput('integer');
  const [loading, setLoading] = useState(false);

  const onSubmit = async ({ costBudget }: { costBudget: number | '' }) => {
    setLoading(true);
    try {
      await setCostBudget(projectId, Number(costBudget) > 0 ? Number(costBudget) : null);
      toast.success(t('common.messages.updateSuccess'));
      notifyDataChanged('expenses');
      onClose();
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DialogShell onClose={onClose} closeDisabled={loading} className="max-w-md">
      <DialogHeader title={t('pmExpenses.budget.title', { ns: 'pages' })} onClose={onClose} closeDisabled={loading} closeLabel={t('common.actions.close')} />
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="p-6">
          <FormFieldWithError label={t('pmExpenses.budget.title', { ns: 'pages' })} error={errors.costBudget?.message} htmlFor="costBudget">
            <Input type="text" id="costBudget" {...budgetInput.bind(register('costBudget', budgetInput.validationRules))} placeholder="0" autoFocus />
          </FormFieldWithError>
          <p className="mt-2 text-[13px] text-fg-secondary">{t('pmExpenses.budget.dialogHint', { ns: 'pages' })}</p>
        </div>
        <DialogFooter>
          <button type="button" onClick={onClose} className={dialogButtonClass.cancel} disabled={loading}>{t('common.actions.cancel')}</button>
          <LoadingButton type="submit" loading={loading} isEditMode t={t} />
        </DialogFooter>
      </form>
    </DialogShell>
  );
};

/**
 * 專案詳情「外部成本」分頁（管理員）：成本預算用量＋外部成本清單
 * 總成本＝人力成本（工時 × 費率）＋外部成本；設了預算時用到 80%、100% 會通知專案負責人
 */
const ProjectExpensesPanel = React.forwardRef<ProjectExpensesPanelRef, Props>(({ projectId, packages }, ref) => {
  const { t, i18n } = useTranslation(['translation', 'pages']);
  const { data: expenses, refetch, key } = useExpensesCache(projectId);
  const profitability = useProfitability(projectId);
  const [editing, setEditing] = useState<PMExpense | 'new' | null>(null);
  const [deleting, setDeleting] = useState<PMExpense | null>(null);
  const [budgetOpen, setBudgetOpen] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refetch(); }, [key]);
  useDataChanged('expenses', refetch);
  useImperativeHandle(ref, () => ({ openCreateDialog: () => setEditing('new') }));

  const tp = (k: string, options?: Record<string, unknown>) => t(`pmExpenses.${k}`, { ns: 'pages', ...options });
  const list = expenses ?? [];
  const expenseTotal = profitability?.expenseCost ?? 0;
  const laborCost = profitability?.laborCost ?? 0;
  const totalCost = profitability?.totalCost ?? 0;
  const budget = profitability?.costBudget ?? null;
  const usage = profitability?.costUsage ?? null;

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await deleteExpense(deleting.id);
      toast.success(t('common.messages.deleteSuccess'));
      notifyDataChanged('expenses');
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setDeleting(null);
    }
  };

  return (
    <>
      <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard title={tp('summary.expenses')} value={<Amount value={expenseTotal} />} hint={tp('summary.count', { count: list.length })} />
        <StatCard
          title={tp('summary.totalCost')}
          value={<Amount value={totalCost} />}
          hint={tp('summary.totalHint', { labor: laborCost.toLocaleString(), expenses: expenseTotal.toLocaleString() })}
        />
        {/* 成本預算：用量條＋設定 */}
        <div className="h-full bg-white dark:bg-slate-800 p-4 rounded-lg shadow dark:shadow-none dark:border dark:border-line">
          <div className="flex items-baseline justify-between gap-2">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">{tp('budget.title')}</h3>
            <button type="button" onClick={() => setBudgetOpen(true)} className="shrink-0 text-[13px] text-accent hover:underline">
              {tp(budget ? 'budget.edit' : 'budget.set')}
            </button>
          </div>
          {budget && usage !== null ? (
            <>
              <div className="mt-1 text-2xl font-semibold text-fg tabular-nums whitespace-nowrap">
                <span className={usageTone(usage)}>{usage}%</span>
                <span className="ml-2 text-base font-normal text-fg-secondary">/ <Amount value={budget} /></span>
              </div>
              <div className="mt-2 h-1.5 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden" role="progressbar" aria-valuenow={usage} aria-valuemin={0} aria-valuemax={100}>
                <div className={`h-full rounded-full ${barTone(usage)}`} style={{ width: `${Math.min(100, usage)}%` }} />
              </div>
            </>
          ) : (
            <>
              <div className="mt-1 text-2xl font-semibold text-fg-muted">—</div>
              <p className="mt-1 text-[13px] text-fg-secondary truncate">{tp('budget.hint')}</p>
            </>
          )}
        </div>
      </div>

      {expenses === undefined ? (
        <TableSkeleton rows={4} columns={6} />
      ) : (
        <div className="bg-surface shadow dark:shadow-none dark:border dark:border-line overflow-hidden rounded-lg">
          <div className="overflow-x-auto scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            <Table style={{ tableLayout: 'fixed', minWidth: '820px' }}>
              <TableHeader>
                <TableRow>
                  <TableHead numeric className="w-32">{tp('fields.expenseDate')}</TableHead>
                  <TableHead className="w-28">{tp('fields.category')}</TableHead>
                  <TableHead className="w-40">{tp('fields.vendor')}</TableHead>
                  <TableHead>{tp('fields.description')}</TableHead>
                  <TableHead numeric className="w-32">{tp('fields.amount')}</TableHead>
                  <TableHead className="w-28">{t('common.table.columns.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.length === 0 ? (
                  <ListEmptyRow colSpan={6} filtered={false} title={tp('empty')} hint={tp('emptyHint')} />
                ) : (
                  list.map(expense => (
                    <TableRow key={expense.id}>
                      <TableCell numeric>{formatTableDate(expense.expenseDate, i18n.language)}</TableCell>
                      <TableCell>{tp(`category.${expense.category}`)}</TableCell>
                      <TableCell className="truncate">{expense.vendor || <span className="text-fg-muted">—</span>}</TableCell>
                      <TableCell className="whitespace-normal">
                        <div>{expense.description || <span className="text-fg-muted">—</span>}</div>
                        {expense.package && packages.length > 1 && <div className="mt-0.5 text-[13px] text-fg-secondary">{expense.package.name}</div>}
                      </TableCell>
                      <TableCell numeric className="font-semibold"><Amount value={expense.amount} /></TableCell>
                      <TableCell>
                        <RowActions>
                          <RowAction onClick={() => setEditing(expense)}>{t('common.actions.edit')}</RowAction>
                          <RowAction tone="danger" onClick={() => setDeleting(expense)}>{t('common.actions.delete')}</RowAction>
                        </RowActions>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {editing && (
        <ExpenseDialog projectId={projectId} expense={editing === 'new' ? undefined : editing} packages={packages} onClose={() => setEditing(null)} />
      )}
      {budgetOpen && <CostBudgetDialog projectId={projectId} current={budget} onClose={() => setBudgetOpen(false)} />}
      <ConfirmModal
        isOpen={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        title={tp('deleteConfirmTitle')}
        message={tp('deleteConfirmMessage')}
        confirmText={t('common.actions.delete')}
        cancelText={t('common.actions.cancel')}
        type="danger"
        t={t}
      />
    </>
  );
});

ProjectExpensesPanel.displayName = 'ProjectExpensesPanel';

export default ProjectExpensesPanel;
